import { networkIconFor } from '@alison/ui';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import { alpha, type Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { formatPercent } from '../../lib/format.js';
import { CreativeRows } from './CreativeRows.js';
import {
  type Combo,
  type ComboElement,
  categoryRowsOf,
  chipLabelOf,
  comboTitleOf,
  elementLabelOf,
} from './comboRows.js';
import { CategoryLabel, ElementTag } from './ElementTag.js';
import {
  CARD_SX,
  ExpandToggle,
  type GemPicks,
  ONE_LINE,
  SPEND,
  UPLIFT,
  UPLIFT_SX,
  VsAvgPill,
} from './GemCard.js';
import { CHECKBOX_SX, Pill } from './Pill.js';
import { columnsOf, formatValue, type Metric, type ResultRow } from './resultRows.js';

const SUBTLE_SX = { fontSize: 11, color: 'text.secondary' };

const FIGURE_LABEL_SX = { fontSize: 9, fontWeight: 600, color: 'text.secondary' };

const EXPANDER_SX = { gap: 0.25, p: 0 };

const EXPANDER_TEXT_SX = { fontSize: 12, fontWeight: 500 };

const CAVEAT_SX = (theme: Theme) => ({
  gap: 0.75,
  px: 1.25,
  py: 0.75,
  borderRadius: 1,
  bgcolor: alpha(theme.palette.warning.main, 0.12),
  color: theme.palette.warning.dark,
});

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function formatted(metric: Metric): string {
  return formatValue(metric.value, metric.format, metric.currencySymbol ?? '$', metric.precision);
}

function lift(value: number | null): string {
  return value === null ? '' : `${value < 0 ? '↓' : '↑'} ${formatPercent(Math.abs(value), 0)}`;
}

function labelsOf(elements: ComboElement[]): string {
  return elements.map(chipLabelOf).join(' + ');
}

function CategoryRows({ elements }: { elements: ComboElement[] }): ReactNode {
  return (
    <Stack sx={{ gap: 1.25, minWidth: 0 }}>
      {categoryRowsOf(elements).map((row) => (
        <Stack key={row.category} direction="row" sx={{ gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ width: 86, height: 22, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            <CategoryLabel category={row.category} />
          </Box>
          <Stack direction="row" sx={{ gap: 0.75, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {row.tags.map((tag) => (
              <ElementTag key={tag.key} label={tag.tag} title={tag.title} scenes={tag.scenes} />
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}

function Figures({ combo }: { combo: Combo }): ReactNode {
  const uplift = combo.metrics.find((metric) => metric.metric === UPLIFT);
  const spend = combo.metrics.find((metric) => metric.metric === SPEND);
  const kpi = combo.metrics.find((metric) => metric.metric !== UPLIFT && metric.metric !== SPEND);
  return (
    <Stack direction="row" sx={{ gap: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
      {kpi === undefined ? null : (
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <Typography sx={FIGURE_LABEL_SX}>{kpi.metric.toUpperCase()}</Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 500 }}>{formatted(kpi)}</Typography>
          <VsAvgPill value={uplift?.value ?? null} />
        </Stack>
      )}
      {spend === undefined ? null : (
        <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center' }}>
          <Typography sx={FIGURE_LABEL_SX}>{spend.metric.toUpperCase()}</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{formatted(spend)}</Typography>
          {combo.spendShare === null ? null : (
            <Typography sx={SUBTLE_SX}>({formatPercent(combo.spendShare, 1)})</Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

function Checks({ checks }: { checks: Metric[] }): ReactNode {
  if (checks.length === 0) return null;
  return (
    <Stack
      direction="row"
      data-testid="combo-checks"
      sx={{ columnGap: 1.25, rowGap: 0.5, flexWrap: 'wrap' }}
    >
      {checks.map((check) => (
        <Typography key={check.metric} sx={SUBTLE_SX}>
          {check.metric}{' '}
          <Box
            component="span"
            sx={{
              fontWeight: 600,
              color: (check.value ?? 0) < 0 ? 'error.main' : 'success.dark',
            }}
          >
            {lift(check.value)}
          </Box>
        </Typography>
      ))}
    </Stack>
  );
}

function VariantList({ combo }: { combo: Combo }): ReactNode {
  return (
    <Stack data-testid="combo-variants" sx={{ gap: 1 }}>
      {combo.variants.map((variant) => (
        <Stack
          key={variant.key}
          direction="row"
          sx={{ gap: 1.5, alignItems: 'center', minWidth: 0 }}
        >
          <Tooltip title={variant.elements.map(elementLabelOf).join(' + ')}>
            <Typography sx={{ ...SUBTLE_SX, ...ONE_LINE, flex: 1, minWidth: 0 }}>
              {labelsOf(variant.elements)}
            </Typography>
          </Tooltip>
          <Typography sx={SUBTLE_SX}>{plural(variant.support, 'creative')}</Typography>
          <Typography sx={{ ...SUBTLE_SX, color: 'success.dark', fontWeight: 600 }}>
            {lift(variant.uplift)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export function ComboCard({
  combo,
  elementCounts,
  picks,
  onOpen,
}: {
  combo: Combo;
  // How many cards in the list hold each element, so the title leads with a distinct one.
  elementCounts?: Map<string, number>;
  picks?: GemPicks;
  onOpen?: (row: ResultRow) => void;
}): ReactNode {
  const [open, setOpen] = useState<'creatives' | 'variants' | null>(null);
  const toggle = (panel: 'creatives' | 'variants') => setOpen(open === panel ? null : panel);
  const NetworkIcon = networkIconFor(combo.network);
  const support = combo.support;
  const picked = picks?.picked.includes(combo.key) ?? false;
  const showCorrelated =
    combo.correlatedNote !== null && combo.elements.some((element) => element.correlated);
  const showVariants = combo.variantsLabel !== null && combo.variants.length > 0;

  return (
    <Stack
      data-testid={`combo-${combo.key}`}
      sx={{
        ...CARD_SX,
        gap: 1.25,
        pt: 2,
        px: 2,
        pb: 1.5,
        borderRadius: 2,
        borderColor: picked ? 'primary.main' : 'divider',
      }}
    >
      <Stack direction="row" sx={{ gap: 1.25, alignItems: 'flex-start' }}>
        {picks === undefined ? null : (
          <Checkbox
            size="small"
            checked={picked}
            onChange={() => picks.onToggle(combo.key)}
            sx={CHECKBOX_SX}
            slotProps={{ input: { 'aria-label': combo.elements.map(elementLabelOf).join(' + ') } }}
          />
        )}
        <Stack sx={{ gap: 0.25, flex: 1, minWidth: 0 }}>
          <Tooltip title={labelsOf(combo.elements)}>
            <Typography sx={{ ...ONE_LINE, fontSize: 14, fontWeight: 500 }}>
              {comboTitleOf(combo, elementCounts)}
            </Typography>
          </Tooltip>
          <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center', minWidth: 0 }}>
            {NetworkIcon === undefined ? null : (
              <NetworkIcon
                titleAccess={combo.network ?? undefined}
                sx={{ fontSize: 12, flexShrink: 0 }}
              />
            )}
            <Typography sx={{ ...SUBTLE_SX, ...ONE_LINE }}>
              {plural(combo.size, 'element')} · {plural(support, 'creative')}
            </Typography>
          </Stack>
        </Stack>
        {combo.verdict === null ? null : <Pill sx={UPLIFT_SX}>{combo.verdict.toUpperCase()}</Pill>}
      </Stack>

      <Box sx={{ pb: 0.5 }}>
        <CategoryRows elements={combo.elements} />
      </Box>

      <Stack sx={{ gap: 1.25, pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
        <Figures combo={combo} />
        <Checks checks={combo.checks} />
        {showCorrelated ? (
          <Stack direction="row" sx={CAVEAT_SX}>
            <InfoOutlined sx={{ fontSize: 16, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 11, lineHeight: 1.45, color: 'inherit' }}>
              {combo.correlatedNote}
            </Typography>
          </Stack>
        ) : null}
        <Stack direction="row" sx={{ gap: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {showVariants ? (
            <ExpandToggle
              open={open === 'variants'}
              onToggle={() => toggle('variants')}
              sx={EXPANDER_SX}
            >
              <Typography sx={EXPANDER_TEXT_SX}>
                {combo.variantsLabel} ({combo.variants.length + combo.hiddenVariants})
              </Typography>
            </ExpandToggle>
          ) : null}
          {support === 0 ? null : (
            <ExpandToggle
              open={open === 'creatives'}
              onToggle={() => toggle('creatives')}
              sx={EXPANDER_SX}
            >
              <Typography sx={EXPANDER_TEXT_SX}>{plural(support, 'creative')}</Typography>
            </ExpandToggle>
          )}
        </Stack>
        <Collapse in={open === 'variants'} unmountOnExit>
          <VariantList combo={combo} />
        </Collapse>
        <Collapse in={open === 'creatives'} unmountOnExit>
          <CreativeRows
            rows={combo.creatives}
            columns={columnsOf(combo.creatives)}
            plain
            {...(onOpen === undefined ? {} : { onOpen })}
          />
        </Collapse>
      </Stack>
    </Stack>
  );
}
